import React, { useEffect, useRef } from "react";
import { createNotesFromScale, setupVexFlowRenderer, renderNotesWithVexFlow } from "../lib/vexflowUtils";
import { renderIntervalBrackets, clearIntervalHighlight } from "../lib/intervalBrackets";
import { useVisualConfig } from "../hooks";

/**
 * ScaleNotation Component - Handles VexFlow musical notation rendering
 * 
 * @param {Array} scale - Array of scale degrees/intervals
 * @param {Function} onNoteClick - Callback when a note is clicked
 * @param {Array} matchingPattern - Array of interval patterns to highlight
 */
const ScaleNotation = ({ scale, onNoteClick, matchingPattern = null }) => {
  const containerRef = useRef(null);
  const visualConfig = useVisualConfig();

  const renderScale = (container, scale, onNoteClick, visualConfig, matchingPattern) => {
    if (!container) return;
    
    container.innerHTML = "";
    
    // Create notes from scale
    const { staveNotes } = createNotesFromScale(scale);
    
    // Setup VexFlow renderer
    const { context, stave, svgWidth } = setupVexFlowRenderer(container);
    
    // Calculate responsive format width using config
    const formatWidth = Math.min(visualConfig?.notation?.formatWidth || 360, svgWidth - 60);
    
    // Render musical notation
    renderNotesWithVexFlow(staveNotes, context, stave, formatWidth);
    
    // Add interval analysis brackets
    renderIntervalBrackets(container, staveNotes, scale, stave, { matchingPattern });
    
    // Add click handlers to notes
    if (onNoteClick) {
      const svg = container.querySelector('svg');
      if (svg) {
        const noteElements = svg.querySelectorAll('.vf-stavenote');
        noteElements.forEach((noteElement, index) => {
          noteElement.style.cursor = 'pointer';
          noteElement.addEventListener('click', () => onNoteClick(index));
        });
      }
    }
  };

  useEffect(() => {
    if (containerRef.current) {
      renderScale(containerRef.current, scale, onNoteClick, visualConfig, matchingPattern);
    }
  }, [scale, onNoteClick, visualConfig, matchingPattern]);

  // Global click handler to clear interval highlighting
  useEffect(() => {
    const handleGlobalClick = (e) => {
      // Don't clear if clicking on an interval number or note
      if (e.target.classList.contains('interval-number') || 
          e.target.closest('.vf-stavenote') ||
          e.target.closest('.play-button')) {
        return;
      }
      
      // Clear interval highlighting
      if (containerRef.current) {
        clearIntervalHighlight(containerRef.current);
      }
    };

    document.addEventListener('click', handleGlobalClick);
    
    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, []);

  return (
    <div 
      ref={containerRef} 
      className="scale-notation"
    />
  );
};

export default ScaleNotation;